/**
 * Red-team modal (ADR-0088 phase 2a — the dashboard surface).
 *
 * Fires the real red-team endpoint at the site's ACTIVE policy and renders the
 * REAL verdicts — what the merchant's current rule set catches vs lets through,
 * with a one-click "Enable rXX" for each gap that opens the rule editor
 * preselected + prefilled for that rule. This replaces the prototype's
 * SIMULATION (modeled catch-rates) with true results from the live evaluator:
 * shadow evaluation, no settlement, no audit pollution (ADR-0088).
 *
 * The endpoint returns every verdict in one response; we REPLAY them with a
 * staggered live-fire animation (counter, progress bar, streaming log) so the
 * run reads as attacks landing one-by-one — the prototype's "simulating the
 * attack" effect, over real data.
 */

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertOctagon,
  AlertTriangle,
  Check,
  Flame,
  PlayCircle,
  ShieldCheck,
  ShieldOff,
} from 'lucide-react';

import type { SiteId } from '@sill/types';
import type { RedTeamRunResult, RedTeamVerdict } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { ApiError, type PlanLimitInfo } from '../../api/http-client.js';
import { useC } from '../../lib/theme.js';
import { Modal, PlanLimitNotice, Tooltip } from '../../components/atoms/index.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

/** Per-attack reveal cadence for the live-fire replay (ms). ~18 attacks ⇒ ~2.5s. */
const REVEAL_MS = 140;

/**
 * Plain-language explanation of each attack, keyed by scenario_id, shown as a
 * tooltip so a merchant who doesn't know the rule numbers understands what was
 * fired. Covers the 2a policy-layer library + the 2b edge attacks.
 */
const ATTACK_DESCRIPTIONS: Record<string, string> = {
  unknown_agent: 'An agent whose identity is NOT on your allowlist tries to transact. r01 should reject unrecognized agents.',
  no_user_mandate: 'An agent acts directly with no user-signed IntentMandate behind it. r02 requires a delegated, user-authorized mandate.',
  over_spend_cap: 'An order far above your per-transaction ceiling. r05 caps the maximum amount.',
  destructive_unattended: 'A large refund with no human in the loop. r07 routes high-value destructive actions to review.',
  false_urgency: 'A dark-pattern "only 2 left" scarcity cue planted in the order. r08 flags manipulative urgency.',
  drip_pricing: 'The checkout total exceeds the quoted price (hidden fees). r09 catches drip pricing.',
  prompt_injection: 'An "ignore all previous instructions" style injection in the intent. r10 detects instruction-override text.',
  geofenced_origin: 'A request from a country you don\'t serve. r12 geofences by request origin.',
  cart_over_ceiling: 'The cart total exceeds the amount the user actually authorized. r14 enforces the intent ceiling.',
  currency_swap: 'The cart currency differs from the authorized currency. r15 blocks currency swaps.',
  bola_cross_customer: 'An agent tries to read another customer\'s order (BOLA). r17 scopes data to the signed user.',
  manifest_tamper: 'The agent\'s skill-manifest digest doesn\'t match the approved one. r18 rejects tampered/drifted manifests.',
  silent_subscription: 'A one-off purchase silently flipped into a subscription with no consent. r19 catches non-consented recurring charges.',
  unicode_tag_smuggle: 'Invisible Unicode tag characters hidden in a field to smuggle instructions. r20 blocks tag-block smuggling.',
  credential_leak: 'A secret / API-key pattern embedded in an intent field. r22 detects credential leakage.',
  overlong_validity: 'A mandate valid for far too long (e.g. 24h), widening the replay window. r23 caps the validity window.',
  oversized_body: 'An oversized mandate body aimed at resource exhaustion. r25 caps the body size.',
  unproven_delegation: 'An agent claims delegated authority but carries no signed delegation chain proving the hand-off. r29 (with "require chain" on) rejects unproven delegation.',
  benign_control: 'A normal, legitimate order. This SHOULD pass — if your policy blocks it, that is a false positive worth reviewing.',
  edge_bad_signature: 'A forged / tampered signature fired at your LIVE edge. The verifier must reject it.',
  edge_expired: 'An expired mandate fired at your LIVE edge. The verifier must reject it.',
  edge_unknown_agent: 'A mandate from an unregistered agent fired at your LIVE edge. The verifier must reject it.',
  edge_replay: 'The same mandate replayed twice at your LIVE edge. Replay defense (r11) must reject the second.',
};

type Phase = 'idle' | 'running' | 'done';

export interface RedTeamModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly site_id: SiteId | undefined;
  /**
   * The site's raw site_key, when the dashboard has it (remembered from
   * create). ADR-0089 Phase 2b — passing it unlocks the LIVE edge-shadow stage
   * (verifier + replay fired at the deployed edge). Absent ⇒ origin-shadow only.
   */
  readonly site_key?: string;
  /**
   * Configure the named rule (by type) to close a gap. Opens the rule editor
   * preselected + prefilled for that rule type — add-preset when the rule is
   * not in the policy, edit-prefill (and enable) when it is present but off.
   */
  readonly onConfigureRule: (rule_type: string) => void;
  /**
   * The ACTIVE policy's rules (type + enabled), used ONLY to report
   * settlement-layer coverage (r06 / r31). Those rules are enforced at the
   * charge seam, not the edge evaluator, so the shadow run can't fire them —
   * we read their configured state from here instead of faking a verdict.
   * Defaults to empty (⇒ both shown as NOT CONFIGURED).
   */
  readonly activeRules?: readonly { readonly type: string; readonly enabled: boolean }[];
  /**
   * True when the site has a saved draft but no active (published) policy.
   * Tightens the "no active policy" error copy from the generic fallback to
   * "publish your draft" so the user knows they're one click away.
   */
  readonly hasDraft?: boolean;
}

export function RedTeamModal({ open, onClose, site_id, site_key, onConfigureRule, activeRules = [], hasDraft = false }: RedTeamModalProps): JSX.Element {
  const C = useC();
  const api = useApi();
  // Owner/admin-only (server gate: red-team.ts). Mirror it in the UI so a
  // reviewer/viewer sees a clear note instead of a raw 403 on click.
  const auth = useOptionalAuth();
  const role =
    auth !== null && auth.state.kind === 'authenticated' ? auth.state.user.role : null;
  const can_run = role === 'owner' || role === 'admin';
  const [phase, setPhase] = useState<Phase>('idle');
  const [result, setResult] = useState<RedTeamRunResult | null>(null);
  const [revealed, setRevealed] = useState<RedTeamVerdict[]>([]);
  const [error, setError] = useState<string | null>(null);
  // ADR-0143 §8 — same entitlements cap-check shape `DomainStep` handles
  // (assertRedTeamWithinCap, same enforcement toggle). A dedicated notice
  // instead of falling into the generic coral `error` string, which would
  // otherwise show the friendly server message prefixed with the raw
  // `plan_limit_reached:402 —` ApiError.message shape.
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);

  // Reset to a clean slate whenever the modal is closed so a re-open starts
  // fresh rather than flashing the previous run.
  useEffect(() => {
    if (!open) {
      setPhase('idle');
      setResult(null);
      setRevealed([]);
      setError(null);
      setPlanLimit(null);
    }
  }, [open]);

  // Staggered live-fire replay: once the run resolves, reveal one verdict per
  // tick until the full set is shown, then settle into 'done'.
  useEffect(() => {
    if (phase !== 'running' || result === null) return;
    if (revealed.length >= result.verdicts.length) {
      setPhase('done');
      return;
    }
    const t = setTimeout(() => {
      setRevealed(result.verdicts.slice(0, revealed.length + 1));
    }, REVEAL_MS);
    return () => clearTimeout(t);
  }, [phase, result, revealed.length]);

  const launch = async (): Promise<void> => {
    if (site_id === undefined || !can_run) return;
    setPhase('running');
    setError(null);
    setPlanLimit(null);
    setResult(null);
    setRevealed([]);
    try {
      setResult(await api.policy.runRedTeam(site_id, site_key));
    } catch (e) {
      if (e instanceof ApiError && e.kind === 'plan_limit_reached' && e.plan_limit !== undefined) {
        setPlanLimit(e.plan_limit);
        setPhase('idle');
        return;
      }
      // Map the owner/admin 403 to a clear message (fallback — the button
      // is also disabled for non-privileged roles above).
      if (e instanceof ApiError && e.status === 403) {
        setError('Only an owner or admin can run the red team.');
      } else if (
        e instanceof ApiError
        && e.server_reason === 'no_active_policy'
      ) {
        setError(
          hasDraft
            ? 'Your draft is ready — hit Publish in Rules to activate it, then run the red team.'
            : 'This site has no active policy yet, so there is nothing for the '
              + 'red team to attack. Publish a policy in Rules first — or '
              + 'connect a payment rail, which seeds a protective baseline '
              + 'policy automatically.',
        );
      } else {
        setError(e instanceof Error ? e.message : 'Red-team run failed.');
      }
      setPhase('idle');
    }
  };

  const total = result?.verdicts.length ?? 0;
  // Live counts during the reveal animation come from `revealed`; once 'done',
  // the displayed CAUGHT/MISSED come straight from the SAME array the list
  // renders (result.verdicts) so the summary can never disagree with the list
  // (the "15 missed in summary, 14 in the list" report). caught+missed == total
  // by construction here.
  // An edge attack the run couldn't confirm — a transport failure or a replay
  // the per-PoP KV hadn't converged on. NOT a confirmed miss: shown as its own
  // muted state and kept out of the MISSED count so a transient never reads as
  // a real gap (ADR-0089 review).
  const isInconclusive = (v: RedTeamVerdict): boolean =>
    v.outcome === 'edge_unreachable'
    || v.outcome === 'edge_replay_inconclusive'
    || v.outcome === 'edge_ratelimit_inconclusive';
  // The benign control is NOT an attack — it's a legitimate order that SHOULD
  // pass. caught=false on it is the GOOD outcome (it passed), so it must be
  // kept out of the CAUGHT/MISSED attack tallies and rendered as PASS, not a
  // red MISS (the "15 vs 14" the merchant counted: 14 real attacks slipped + 1
  // control correctly passed = the 15th "MISSED").
  const isControl = (v: RedTeamVerdict): boolean => v.category === 'control';
  const dv = result?.verdicts ?? [];
  const revealedCaught = revealed.filter((v) => v.caught && !isControl(v)).length;
  const revealedMissed = revealed.filter((v) => !v.caught && !isInconclusive(v) && !isControl(v)).length;
  const doneCaught = dv.filter((v) => v.caught && !isControl(v)).length;
  const doneMissed = dv.filter((v) => !v.caught && !isInconclusive(v) && !isControl(v)).length;
  const doneInconclusive = dv.filter((v) => isInconclusive(v) && !isControl(v)).length;
  const caughtSoFar = phase === 'done' ? doneCaught : revealedCaught;
  const missedSoFar = phase === 'done' ? doneMissed : revealedMissed;
  const progress = total > 0 ? (revealed.length / total) * 100 : 0;

  // Actionable buckets computed from the full verdicts (each carries
  // `rule_enabled`), so the done view is never a dead end:
  //   - toEnable: missed + the catching rule is OFF      → "Enable rXX" (add it)
  //   - toReview: missed + the catching rule is ON        → "Review rXX" (it's
  //     enabled but didn't stop this — likely mis-tuned or masked by an earlier
  //     rule; opening the editor lets the merchant fix it)
  //   - falsePositive: the benign control was BLOCKED      → a rule is rejecting
  //     legitimate traffic (e.g. an allowlist the live agent isn't on)
  const doneVerdicts = result?.verdicts ?? [];
  const toEnable = doneVerdicts.filter((v) => !v.caught && v.catching_rule !== '—' && !v.rule_enabled);
  const toReview = doneVerdicts.filter((v) => !v.caught && v.catching_rule !== '—' && v.rule_enabled);
  const control = doneVerdicts.find((v) => v.category === 'control');
  const falsePositive = control !== undefined && control.caught ? control : null;
  const allClean = toEnable.length === 0 && toReview.length === 0 && falsePositive === null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Red-team"
      subtitle="Fire real attacks at your live policy and see exactly what it catches — and what slips through. No charges, no test data: the real evaluator runs against your current rules."
      closeOnBackdrop={false}
    >
      <div className="p-6 space-y-5 overflow-x-hidden" style={{ overflowWrap: 'anywhere' }}>
        {phase === 'idle' ? (
          <div className="flex flex-col items-start gap-3">
            <div className="flex items-center gap-2">
              <Flame size={13} color={C.coralFg} />
              <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 2 }}>
                READY
              </span>
            </div>
            <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.6, maxWidth: 520 }}>
              The attack library covers identity, spend, scope, prompt-injection
              and dark-pattern vectors. Each runs against your active policy; a
              miss tells you which rule would stop it.
            </div>
            <Tooltip content={!can_run ? 'Only an owner or admin can run the red team.' : undefined}>
            <button
              type="button"
              onClick={() => void launch()}
              disabled={site_id === undefined || !can_run}
              className="flex items-center justify-center gap-2 px-4 py-2"
              style={{
                background: C.coral,
                color: '#FFFFFF',
                fontFamily: sansFont, fontWeight: 600, fontSize: 13,
                borderRadius: 2, border: `1px solid ${C.coral}`,
                cursor: site_id === undefined || !can_run ? 'not-allowed' : 'pointer',
                opacity: site_id === undefined || !can_run ? 0.5 : 1,
              }}
            >
              <Flame size={13} /> Launch red-team
            </button>
            </Tooltip>
            {!can_run ? (
              <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                Running the red team requires an owner or admin role.
              </div>
            ) : null}
            {planLimit !== null ? (
              // ADR-0143 §8 — same full-page `/?view=...` link pattern as
              // `ShadowEscalationView` in Guardrails.tsx: this modal takes
              // no props from a router, so a plain href avoids drilling
              // `setView` through `Guardrails()` (which takes none today).
              <PlanLimitNotice planLimit={planLimit} viewPlansHref="/?view=billing" />
            ) : error !== null ? (
              <div
                className="p-2 flex items-start gap-2"
                style={{ background: C.coralDim, border: `1px solid ${C.coralFg}`, borderRadius: 2 }}
              >
                <AlertOctagon size={12} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
                <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>{error}</span>
              </div>
            ) : null}
          </div>
        ) : (
          <>
            {/* Stat header — counters + progress (running) or summary (done). */}
            <div className="space-y-3">
              <div className="flex items-center gap-4 flex-wrap">
                {phase === 'running' ? (
                  <motion.div
                    animate={{ opacity: [0.35, 1, 0.35] }}
                    transition={{ duration: 1.3, repeat: Infinity }}
                    className="flex items-center gap-2"
                  >
                    <Flame size={13} color={C.coralFg} />
                    <span style={{ fontFamily: monoFont, fontSize: 9, color: C.coralFg, letterSpacing: 2 }}>
                      RUNNING
                    </span>
                  </motion.div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Check size={13} color={C.mintFg} />
                    <span style={{ fontFamily: monoFont, fontSize: 9, color: C.mintFg, letterSpacing: 2 }}>
                      COMPLETE
                    </span>
                  </div>
                )}
                <Stat C={C} tone="mint" icon={<ShieldCheck size={14} color={C.mintFg} />} label="CAUGHT" value={caughtSoFar} />
                <Stat C={C} tone="coral" icon={<ShieldOff size={14} color={C.coralFg} />} label="MISSED" value={missedSoFar} />
                <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1 }}>
                  {revealed.length} / {total} fired
                </span>
                {phase === 'done' && doneInconclusive > 0 ? (
                  <Tooltip content="Edge attacks that couldn't be confirmed this run (transport hiccup, or a replay the edge KV hadn't converged on). Re-run to confirm — they are not counted as misses.">
                  <span
                    style={{ fontFamily: monoFont, fontSize: 9, color: C.amberFg, letterSpacing: 1, cursor: 'help' }}
                  >
                    · {doneInconclusive} N/A
                  </span>
                  </Tooltip>
                ) : null}
                {phase === 'done' ? (
                  <button
                    type="button"
                    onClick={() => void launch()}
                    className="ml-auto flex items-center gap-2 px-3 py-1.5"
                    style={{
                      fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted,
                      background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 2,
                      cursor: 'pointer',
                    }}
                  >
                    <PlayCircle size={11} /> RE-RUN
                  </button>
                ) : null}
              </div>
              {/* In-flight notice — the request (live edge stage + page fetch)
                  can take a few seconds; make it obviously working, not stuck. */}
              {phase === 'running' && result === null ? (
                <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
                  Firing live attacks at your edge — this can take a few seconds…
                </div>
              ) : null}
              {/* Progress bar — an indeterminate sweep while the request is
                  in-flight, then fills as attacks reveal. */}
              <div style={{ height: 4, background: C.surface2, borderRadius: 2, overflow: 'hidden' }}>
                {phase === 'running' && result === null ? (
                  <motion.div
                    animate={{ x: ['-45%', '165%'] }}
                    transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ height: '100%', width: '40%', background: C.coral, borderRadius: 2 }}
                  />
                ) : (
                  <motion.div
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.2 }}
                    style={{ height: '100%', background: phase === 'done' ? C.mintFg : C.coral }}
                  />
                )}
              </div>
            </div>

            {/* Actionable sections (done only) — never a dead end. */}
            {phase === 'done' && result !== null ? (
              <>
                {/* False-positive: a legitimate order got blocked. */}
                {falsePositive !== null ? (
                  <div
                    className="p-3"
                    style={{ background: C.amberDim, border: `1px solid ${C.amberFg}`, borderRadius: 2 }}
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={14} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
                      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                        <strong>False positive:</strong> your policy blocked the benign
                        control order{falsePositive.caught_by ? <> (by <span style={{ fontFamily: monoFont }}>{falsePositive.caught_by}</span>)</> : null}.
                        A rule is rejecting legitimate traffic — often an allowlist (r01)
                        the calling agent isn’t on. Review that rule so real orders pass.
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Live edge (2b) stage skipped — explain why, never silent. */}
                {result.edge && !result.edge.attempted && result.edge.resolve !== 'disabled' ? (
                  <div
                    className="p-3"
                    style={{ background: C.skyDim, border: `1px solid ${C.skyFg}`, borderRadius: 2 }}
                  >
                    <div className="flex items-start gap-2">
                      <AlertOctagon size={14} color={C.skyFg} className="flex-shrink-0 mt-0.5" />
                      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                        <strong>Live edge tests skipped</strong>{' '}
                        (<span style={{ fontFamily: monoFont }}>{result.edge.resolve}
                        {result.edge.reason ? `: ${result.edge.reason}` : ''}</span>). The
                        forged-signature, replay, and MCP rate-limit (r30) checks read your
                        site’s embed key from your live page; that step didn’t complete, so
                        only the in-process attacks ran.{' '}
                        {result.edge.resolve === 'fetch_failed'
                          ? 'We couldn’t fetch your site (bot-protected or slow) — retry, or route it through the unblocker.'
                          : result.edge.resolve === 'not_found'
                            ? 'No embed key was found on the page (e.g. a hash-only install).'
                            : 'Re-run to retry.'}
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Gaps to ENABLE — missed, the catching rule is off. */}
                {toEnable.length > 0 ? (
                  <div
                    className="p-3"
                    style={{ background: C.coralDim, border: `1px solid ${C.coralFg}`, borderRadius: 2 }}
                  >
                    <div style={{ fontFamily: monoFont, fontSize: 9, color: C.coralFg, letterSpacing: 2, marginBottom: 8 }}>
                      {toEnable.length} GAP{toEnable.length === 1 ? '' : 'S'} — ATTACKS THAT GOT THROUGH
                    </div>
                    <div className="space-y-2">
                      {toEnable.map((v) => (
                        <div key={v.scenario_id} className="flex items-center gap-2 flex-wrap">
                          {ATTACK_DESCRIPTIONS[v.scenario_id] ? (
                            <Tooltip
                              content={ATTACK_DESCRIPTIONS[v.scenario_id]}
                              side="top"
                              displayMode="inline"
                              style={{ fontFamily: sansFont, fontSize: 12, color: C.text, flex: 1, minWidth: 'min(180px, 100%)' }}
                            >
                              {v.name}
                            </Tooltip>
                          ) : (
                            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, flex: 1, minWidth: 'min(180px, 100%)' }}>
                              {v.name}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => onConfigureRule(v.catching_rule)}
                            className="px-2.5 py-1"
                            style={{
                              fontFamily: monoFont, fontSize: 10, letterSpacing: 0.5,
                              color: C.ink, background: C.lime,
                              border: `1px solid ${C.lime}`, borderRadius: 2,
                              cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                          >
                            Enable {v.catching_rule}
                          </button>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5, marginTop: 8 }}>
                      "Enable" opens the rule editor preselected + prefilled — review the
                      defaults, add it to your draft, then Publish to enforce it.
                    </div>
                  </div>
                ) : null}

                {/* Misconfigured — missed, but the catching rule IS on. */}
                {toReview.length > 0 ? (
                  <div
                    className="p-3"
                    style={{ background: C.amberDim, border: `1px solid ${C.amberFg}`, borderRadius: 2 }}
                  >
                    <div style={{ fontFamily: monoFont, fontSize: 9, color: C.amberFg, letterSpacing: 2, marginBottom: 8 }}>
                      {toReview.length} RULE{toReview.length === 1 ? '' : 'S'} ON BUT DIDN’T CATCH — REVIEW
                    </div>
                    <div className="space-y-2">
                      {toReview.map((v) => (
                        <div key={v.scenario_id} className="flex items-center gap-2 flex-wrap">
                          {ATTACK_DESCRIPTIONS[v.scenario_id] ? (
                            <Tooltip
                              content={ATTACK_DESCRIPTIONS[v.scenario_id]}
                              side="top"
                              displayMode="inline"
                              style={{ fontFamily: sansFont, fontSize: 12, color: C.text, flex: 1, minWidth: 'min(180px, 100%)' }}
                            >
                              {v.name}
                            </Tooltip>
                          ) : (
                            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, flex: 1, minWidth: 'min(180px, 100%)' }}>
                              {v.name}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => onConfigureRule(v.catching_rule)}
                            className="px-2.5 py-1"
                            style={{
                              fontFamily: monoFont, fontSize: 10, letterSpacing: 0.5,
                              color: C.amberFg, background: 'transparent',
                              border: `1px solid ${C.amberFg}`, borderRadius: 2,
                              cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                          >
                            Review {v.catching_rule}
                          </button>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5, marginTop: 8 }}>
                      These rules are enabled but didn’t stop the attack — usually a
                      setting to tighten, or an earlier rule (e.g. an allowlist) matching
                      first. "Review" opens the rule so you can adjust it.
                    </div>
                  </div>
                ) : null}

                {/* All clear. */}
                {allClean ? (
                  <div
                    className="p-3 flex items-center gap-2"
                    style={{ background: C.mintDim, border: `1px solid ${C.mintFg}`, borderRadius: 2 }}
                  >
                    <ShieldCheck size={14} color={C.mintFg} />
                    <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>
                      No gaps — every attack with a configured guardrail was caught, and
                      the benign control passed.
                    </span>
                  </div>
                ) : null}
              </>
            ) : null}

            {/* Live log — streams during the run, full list when done. */}
            <div>
              <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>
                {phase === 'running' ? 'LIVE LOG' : 'ALL ATTACKS'}
              </div>
              <div className="space-y-1.5">
                <AnimatePresence initial={false}>
                  {(phase === 'done' && result !== null ? result.verdicts : [...revealed].reverse()).map((v) => (
                    <motion.div
                      // Stable key (scenario_ids are server-guaranteed unique, no
                      // double-push) so the running→done reorder MOVES rows rather
                      // than remounting them — no flicker. Count integrity no
                      // longer depends on the key; it's derived from the verdicts.
                      key={v.scenario_id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.18 }}
                      className="flex items-center gap-2 flex-wrap"
                    >
                      <span
                        style={{
                          fontFamily: monoFont, fontSize: 9, letterSpacing: 1, fontWeight: 600,
                          // Control: PASS (good) when it slipped through, BLOCKED
                          // (bad — a false positive) when the policy stopped it.
                          color: isControl(v)
                            ? (v.caught ? C.coralFg : C.mintFg)
                            : v.caught ? C.mintFg : isInconclusive(v) ? C.amberFg : C.coralFg,
                          background: isControl(v)
                            ? (v.caught ? C.coralDim : C.mintDim)
                            : v.caught ? C.mintDim : isInconclusive(v) ? C.amberDim : C.coralDim,
                          padding: '2px 6px', borderRadius: 2, flexShrink: 0, minWidth: 56, textAlign: 'center',
                        }}
                      >
                        {isControl(v)
                          ? (v.caught ? 'BLOCKED' : 'PASS')
                          : v.caught ? 'CAUGHT' : isInconclusive(v) ? 'N/A' : 'MISSED'}
                      </span>
                      {ATTACK_DESCRIPTIONS[v.scenario_id] ? (
                        <Tooltip
                          content={ATTACK_DESCRIPTIONS[v.scenario_id]}
                          side="top"
                          displayMode="inline"
                          style={{ fontFamily: sansFont, fontSize: 12, color: C.text, flex: 1, minWidth: 'min(160px, 100%)' }}
                        >
                          {v.name}
                        </Tooltip>
                      ) : (
                        <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, flex: 1, minWidth: 'min(160px, 100%)' }}>
                          {v.name}
                        </span>
                      )}
                      <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 0.5 }}>
                        {isControl(v)
                          ? (v.caught ? `false positive · by ${v.caught_by ?? '?'}` : 'passed — expected')
                          : v.caught
                            ? `by ${v.caught_by ?? '?'}`
                            : isInconclusive(v)
                              ? v.outcome === 'edge_unreachable'
                                ? 'edge unreachable'
                                // A distributed edge rate-limit (r30) can't be
                                // deterministically confirmed from one probe, so
                                // don't invite endless re-runs — it's always-on
                                // and enforced at the edge regardless.
                                : v.outcome === 'edge_ratelimit_inconclusive'
                                  ? 'always-on · edge-enforced'
                                  : 'inconclusive · re-run'
                              : v.catching_rule === '—'
                                ? '—'
                                : `needs ${v.catching_rule}`}
                      </span>
                      {/* Framework ids the attack exercises (mapping evidence —
                          only ids confirmed in docs/security/framework-mappings.md). */}
                      {v.frameworks?.map((f) => (
                        <span
                          key={f}
                          style={{
                            fontFamily: monoFont, fontSize: 8, letterSpacing: 0.5, color: C.muted,
                            border: `1px solid ${C.border}`, padding: '1px 4px', borderRadius: 2, flexShrink: 0,
                          }}
                        >
                          {f}
                        </span>
                      ))}
                      {/* CWE ids the attack exercises — standardized, third-party-
                          legible findings (docs/security/framework-mappings.md). */}
                      {v.cwe?.map((c) => (
                        <span
                          key={c}
                          data-testid={`redteam-cwe-${v.scenario_id}-${c}`}
                          style={{
                            fontFamily: monoFont, fontSize: 8, letterSpacing: 0.5, color: C.muted,
                            border: `1px solid ${C.border}`, padding: '1px 4px', borderRadius: 2, flexShrink: 0,
                          }}
                        >
                          {c}
                        </span>
                      ))}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* ADR-0089 §4 m4 — honest coverage note. Distinguishes what was
                fired LIVE at the edge from what is verified elsewhere, so a
                merchant never reads "no edge gaps" as "geofence + lockout
                fully tested." */}
            {phase === 'done' && result !== null && result.verdicts.some((v) => v.scenario_id.startsWith('edge_')) ? (
              <div
                style={{
                  fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.6,
                  borderTop: `1px solid ${C.border}`, paddingTop: 10,
                }}
              >
                Edge-layer attacks (signature, expiry, unknown agent, replay) were
                fired live at your deployed edge. Geofence (r12) is verified at the
                policy layer, and the failed-auth lockout <em>trigger</em> (r24,
                5 failures/60s) is covered by the operator burst test — neither is
                simulated from this probe's location, so they are not shown as edge
                gaps here.
              </div>
            ) : null}

            {/* Settlement-layer coverage — r06 / r31 are enforced at the CHARGE
                seam, not the edge evaluator, so the shadow run can't fire them.
                Reported from the ACTIVE policy (present + enabled?), visually
                distinct from a fired verdict, and excluded from the tally. */}
            {phase === 'done' && result !== null ? (
              <SettlementLayerStrip
                C={C}
                activeRules={activeRules}
                onConfigureRule={onConfigureRule}
              />
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * Settlement-layer rules (r06 per-user 24h cap, r31 cross-agent org cap) fire
 * at the rail charge seam against a Postgres rolling-spend aggregate — NOT the
 * edge evaluator the red-team shadow run drives. So the run can't exercise
 * them, and a green "BLOCKED" verdict would be a lie. Instead we report each
 * rule's status from the ACTIVE policy, in a form that can NEVER be mistaken
 * for a fired-attack verdict:
 *   - present + enabled → neutral card, mint accent, "ACTIVE · ENFORCED AT THE
 *     CHARGE" + a prominent "NOT SHADOW-TESTED" tag (the honesty-critical
 *     word). No CAUGHT/BLOCKED verdict pill.
 *   - absent / disabled → AMBER (not coral — coral means "an attack got
 *     through", which is false since nothing fired), labelled "NOT FIRED", the
 *     concrete threat, and the same lime "Enable rXX" affordance as a shadow
 *     gap.
 * Kept OUT of the CAUGHT/MISSED tally (those count fired attacks).
 */
const SETTLEMENT_LAYER_RULES: readonly {
  readonly type: 'r06' | 'r31';
  readonly title: string;
  readonly activeCopy: string;
  readonly gapThreat: string;
}[] = [
  {
    type: 'r31',
    title: 'Cross-agent org spend cap',
    activeCopy:
      'Enforced at settlement against the organization’s rolling spend across every rail — not exercisable from a shadow run. Cap rejections appear in your audit log.',
    gapThreat:
      'Multiple agents under one organization can collectively overspend across sessions — no rolling org cap is enforced.',
  },
  {
    type: 'r06',
    title: 'Per-user 24h spend cap',
    activeCopy:
      'Enforced at settlement against the user’s trailing-24h spend across every rail — not exercisable from a shadow run. Cap rejections appear in your audit log.',
    gapThreat:
      'A single user’s agents can exceed a safe daily spend across sessions — no trailing-24h cap is enforced.',
  },
];

function SettlementLayerStrip({
  C,
  activeRules,
  onConfigureRule,
}: {
  C: ReturnType<typeof useC>;
  activeRules: readonly { readonly type: string; readonly enabled: boolean }[];
  onConfigureRule: (rule_type: string) => void;
}): JSX.Element {
  return (
    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
      <div
        style={{
          fontFamily: monoFont, fontSize: 9, color: C.skyFg, letterSpacing: 2, marginBottom: 6,
        }}
      >
        SETTLEMENT-LAYER COVERAGE
      </div>
      <div
        style={{
          fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.6, marginBottom: 10,
        }}
      >
        These rules are enforced at the charge, not at the edge — the shadow run can’t
        fire them. Status below is read from your active policy, not attacked, and is
        excluded from the counts above. Enforcement is verified in your audit log.
      </div>
      <div className="space-y-2">
        {SETTLEMENT_LAYER_RULES.map((r) => {
          const rule = activeRules.find((x) => x.type === r.type);
          const active = rule !== undefined && rule.enabled;
          return active ? (
            <div
              key={r.type}
              className="p-3"
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderLeft: `3px solid ${C.mintFg}`,
                borderRadius: 2,
              }}
            >
              <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 6 }}>
                <span
                  style={{
                    fontFamily: monoFont, fontSize: 9, color: C.mintFg, letterSpacing: 1.5, fontWeight: 600,
                  }}
                >
                  {r.type.toUpperCase()} · ACTIVE · ENFORCED AT THE CHARGE
                </span>
                {/* Honesty-critical tag — same visual weight as the state
                    label so a fast scan can't read this as a fired-and-held
                    attack. */}
                <span
                  style={{
                    fontFamily: monoFont, fontSize: 9, color: C.skyFg, letterSpacing: 1.5, fontWeight: 600,
                    background: C.skyDim, padding: '1px 6px', borderRadius: 2,
                  }}
                >
                  NOT SHADOW-TESTED
                </span>
              </div>
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                <strong>{r.title}.</strong> {r.activeCopy}
              </div>
            </div>
          ) : (
            <div
              key={r.type}
              className="p-3"
              style={{ background: C.amberDim, border: `1px solid ${C.amberFg}`, borderRadius: 2 }}
            >
              <div
                style={{
                  fontFamily: monoFont, fontSize: 9, color: C.amberFg, letterSpacing: 1.5, marginBottom: 8, fontWeight: 600,
                }}
              >
                {r.type.toUpperCase()} · NOT CONFIGURED · RECOMMENDED · NOT FIRED
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  /* minWidth uses `min(180px, 100%)` — defense against horizontal
                     bleed when the settlement-layer amber card renders inside a
                     mobile modal (~214px content area at 320px viewport). */
                  style={{
                    fontFamily: sansFont, fontSize: 12, color: C.text, flex: 1, minWidth: 'min(180px, 100%)', lineHeight: 1.5,
                  }}
                >
                  <strong>{r.title}.</strong> {r.gapThreat}
                </span>
                <button
                  type="button"
                  onClick={() => onConfigureRule(r.type)}
                  className="px-2.5 py-1"
                  style={{
                    fontFamily: monoFont, fontSize: 10, letterSpacing: 0.5,
                    color: C.ink, background: C.lime,
                    border: `1px solid ${C.lime}`, borderRadius: 2,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  Enable {r.type}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({
  C, tone, icon, label, value,
}: {
  C: ReturnType<typeof useC>;
  tone: 'mint' | 'coral';
  icon: React.ReactNode;
  label: string;
  value: number;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <span style={{ fontFamily: serifFont, fontSize: 22, fontWeight: 600, color: tone === 'mint' ? C.mintFg : C.coralFg, lineHeight: 1 }}>
        {value}
      </span>
      <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5 }}>{label}</span>
    </div>
  );
}
